import { Observable } from 'rxjs/Observable';

import 'rxjs/add/operator/mergeMap';
import 'rxjs/add/operator/expand';
import 'rxjs/add/operator/delay';
import 'rxjs/add/observable/of';
import 'rxjs/add/observable/throw';
import 'rxjs/add/operator/repeat';
import 'rxjs/add/observable/timer';

/**
 * Defines function interface to retrieve result of asynchronous operation.
 */
export type RetrieveFunctionType = () => Observable<any>;

/**
 * Defines function interface that should return boolean flag indicating that operation has finished.
 */
export type RepeatFunctionType = (T) => boolean;

/**
 * Enum defines polling outcomes.
 */
export enum PollCheckerStatus {

    /**
     * Async operation finished.
     */
    FINISHED = 'FINISHED',

    /**
     * Async operation has not finished after reaching max attempts.
     */
    REACHED_MAX_ATTEMPTS = 'REACHED_MAX_ATTEMPTS'
}

/**
 * Interface defines values emitted by poll-checker function.
 */
export interface PollCheckerResult<T> {

    /**
     * Status. See PollCheckerStatus.
     */
    status: PollCheckerStatus;

    /**
     * Last result of RetrieveFunctionType invocation.
     */
    result: T;
}

/**
 * Helper function repeats retrieveFunctionType call until repeatFunctionType returns false but only max times.
 * Emits one value PollCheckerResult with PollCheckerStatus.
 *
 * If retrieveFunctionType returns result for which repeatFunctionType returns false then PollCheckerResult with
 * PollCheckerStatus.FINISHED is returned.
 * It max invocations of retrieveFunctionType is reached without repeatFunctionType returning false, then PollCheckerResult
 * with PollCheckerStatus.REACHED_MAX_ATTEMPTS is returned.
 *
 * @param {RetrieveFunctionType} retrieveFunctionType - function must return observable that emits values (REST responses)
 * @param {RepeatFunctionType} repeatFunctionType - function defines if async operation finished; returns true / false;
 * it gets result of retrieveFunctionType invocation
 * @param {number} initialDelay initial delay
 * @param {number} repeatDelay delay between invocations
 * @param {number} max maximum number of retrieveFunctionType calls
 * @return {Observable<PollCheckerResult<T>>} Observable of PollCheckerResult<T>
 */
export function pollChecker<T>(retrieveFunctionType: RetrieveFunctionType,
                            repeatFunctionType: RepeatFunctionType,
                            initialDelay: number, repeatDelay: number, max: number): Observable<PollCheckerResult<T>> {
    let count = 0;
    let retrieve = true;

    return Observable.of('start')
        .delay(initialDelay)
        .mergeMap(() =>
            retrieveFunctionType().expand(() => {

                if (retrieve) {
                    return Observable.of(null)
                        .delay(repeatDelay)
                        .filter(() => retrieve)
                        .mergeMap(() => retrieveFunctionType());
                } else {
                    return Observable.empty();
                }
            })
        )
        .mergeMap(val => {
            count++;

            let repeat = repeatFunctionType(val);

            if (repeat  &&  count < max) {
                return Observable.empty();
            } else if (repeat  &&  count === max) {
                retrieve = false;
                return Observable.of(<PollCheckerResult<T>>{
                    status: PollCheckerStatus.REACHED_MAX_ATTEMPTS,
                    result: val
                });
            } else if (!repeat) {
                retrieve = false;
                return Observable.of(<PollCheckerResult<T>>{
                    status: PollCheckerStatus.FINISHED,
                    result: val
                });
            }

            return Observable.throw('ERROR');
        });
}




// WEBPACK FOOTER //
// ./~/angular2-template-loader!./~/@angularclass/hmr-loader!./~/angular-router-loader/src!./~/tslint-loader!./src/app/core/helpers/poll-checker.helper.ts